import { v4 as uuid } from "uuid";
import * as t from "io-ts";
import getPlaceDetails, { PlaceDetailsError } from "./get-place-details";
const optional = (codec) => t.union([t.undefined, codec]);
const googlePredictionT = t.strict({
    description: t.string,
    place_id: t.string,
    structured_formatting: t.strict({
        main_text: t.string,
        main_text_matched_substrings: optional(t.array(t.strict({
            length: t.number,
            offset: t.number,
        }))),
        secondary_text: t.union([t.undefined, t.string]),
        secondary_text_matched_substrings: optional(t.array(t.strict({
            length: t.number,
            offset: t.number,
        }))),
    }),
});
const googleAutoCompleteResponseOkT = t.strict({
    status: t.literal("OK"),
    predictions: t.array(googlePredictionT),
});
const googleAutoCompleteResponseNonOkT = t.strict({
    status: t.union([
        t.literal("ERROR"),
        t.literal("ZERO_RESULTS"),
        t.literal("INVALID_REQUEST"),
    ]),
});
const googleAutoCompleteResponseT = t.union([
    googleAutoCompleteResponseOkT,
    googleAutoCompleteResponseNonOkT,
]);
export class GooglePlaceSearch {
    sessiontoken;
    options;
    constructor(options) {
        this.sessiontoken = uuid();
        this.options = options;
    }
    async search(searchTerm, searchLocation, urlPrefix) {
        const onError = this.options.onError ||
            ((err) => {
                if (process.env.NODE_ENV === "production") {
                    console.error("Google search API error", { err });
                }
            });
        const onSuccess = this.options.onSuccess ||
            (() => {
                if (process.env.NODE_ENV === "production") {
                    console.log("Google lookup successful");
                }
            });
        const { lat, lon } = searchLocation.position;
        const radius = 30000;
        const types = [];
        const typesQuery = types.join("|");
        const apiKey = this.options.apiKey;
        const input = { searchTerm, searchLocation };
        if (!searchTerm.trim()) {
            return [];
        }
        const url = `${urlPrefix ?? ""}/maps/api/place/autocomplete/json?input=${encodeURIComponent(searchTerm)}&location=${lat},${lon}&radius=${radius}&types=${typesQuery}&sessiontoken=${this.sessiontoken}&key=${apiKey}`;
        const result = await fetch(url).catch((err) => {
            onError({ type: "ERR_NETWORK_ERROR", err, input });
            throw err;
        });
        if (!result.ok) {
            if (result.status < 500) {
                onError({ type: "ERR_GOOGLE_ERROR", status: result.status, input });
            }
            throw new Error(`Autocomplete failed with HTTP ${result.status}`);
        }
        const jsonResponse = await result.json();
        const decodeResult = googleAutoCompleteResponseT.decode(jsonResponse);
        switch (decodeResult._tag) {
            case "Left": {
                onError({
                    type: "ERR_PARSE_ERROR",
                    data: decodeResult.left,
                    input,
                });
                throw new Error("Unrecognized result from google");
            }
            case "Right": {
                const data = decodeResult.right;
                switch (data.status) {
                    case "INVALID_REQUEST": {
                        onError({
                            type: "ERR_GOOGLE_ERROR",
                            data,
                            input,
                        });
                        return [];
                    }
                    case "ZERO_RESULTS":
                        return [];
                    case "OK":
                        return data.predictions.map((prediction) => {
                            return {
                                id: prediction.place_id,
                                label: prediction.description,
                                title: {
                                    label: prediction.structured_formatting.main_text,
                                    highlights: (prediction.structured_formatting
                                        .main_text_matched_substrings || []).map((x) => ({
                                        start: x.offset,
                                        end: x.offset + x.length,
                                    })),
                                },
                                address: {
                                    label: prediction.structured_formatting.secondary_text || "",
                                    highlights: (prediction.structured_formatting
                                        .secondary_text_matched_substrings || []).map((x) => ({
                                        start: x.offset,
                                        end: x.offset + x.length,
                                    })),
                                },
                                lookup: async () => {
                                    const sessiontoken = this.sessiontoken;
                                    this.sessiontoken = uuid();
                                    return getPlaceDetails(prediction.place_id, sessiontoken, apiKey, urlPrefix)
                                        .catch((err) => {
                                        if (err instanceof PlaceDetailsError &&
                                            err.status === "NOT_FOUND") {
                                            throw err;
                                        }
                                        onError({
                                            type: "ERR_PLACELOOKUP",
                                            err,
                                            message: err instanceof Error ? err.message : String(err),
                                            status: err instanceof PlaceDetailsError
                                                ? err.status
                                                : undefined,
                                            input,
                                        });
                                        throw err;
                                    })
                                        .then((x) => {
                                        onSuccess();
                                        return x;
                                    });
                                },
                            };
                        });
                    default:
                        throw new Error("Unrecognized data");
                }
            }
        }
    }
}
//# sourceMappingURL=index.js.map